import { formatPhp, formatDate } from "@/lib/calc";

const BANK_ADDRESS =
  "PJL Building, General Evangelista Street Corner Emilio Aguinaldo Highway, Zapote 1, Bacoor City, Cavite, Philippines";

const PARA_1 =
  'For value received, the undersigned (the "Borrower/Grantor") jointly and severally promise to pay to the order of Cebuana Lhuillier Rural Bank, Inc. (the "Lender" or the "Bank"), at its office at ' +
  BANK_ADDRESS +
  ', or any of its branches or designated payment channels, the Loan Granted stated in Philippine currency (the "Loan"), and the corresponding Finance Charges from the date hereof until paid, according to the Loan Terms set as follows:';

const PARA_2 =
  'The Borrower/Grantor acknowledges that the detailed terms, including the additional and/or conditional charges, repayment dates, installment amounts, and breakdown of principal and interest are fully set forth in the Disclosure Statement hereto attached and made an integral part hereof. The purpose/s of the Loan is/are for Personal Consumption. The Borrower/Grantor shall not use the Loan for any other purposes; otherwise, the Lender may terminate the Loan and immediately demand repayment of the outstanding balance thereof. In exchange for the granting of the Loan, the Borrower/Grantor shall surrender the physical possession of his/her Jewelry as specifically detailed in the corresponding Appraisal Card hereto attached as Annex "B" ("Collateral") in favor of the Lender. This act shall automatically create a first and preferred security interest over the said property to be covered under this Agreement. All obligations considered secured by this Agreement are hereinafter collectively referred to as the "Secured Obligations." The Borrower/Grantor acknowledges that this Promissory Note and Security Agreement is subject to the terms and conditions set forth in Annex "A" (Terms and Conditions) attached hereto and made an integral part hereof. The Borrower/Grantor confirms having read, understood, and agreed to all the provisions contained in Annex "A" and agrees to be bound thereby.';

function SigLine({ caption, value }) {
  return (
    <div className="mt-8">
      <div className="border-t border-slate-900 pt-1 min-h-[1.5rem]">
        <span className="text-base text-slate-900">{value || "\u00A0"}</span>
      </div>
      <p className="text-[11px] font-semibold text-slate-700 leading-tight mt-1">{caption}</p>
    </div>
  );
}

export default function PnsaPage({
  form,
  set,
  contractualRatePct,
  interestAmount,
  netProceeds,
  eirPct,
  monthlyAmort,
  firstPaymentDate,
  loanAmount,
  dst,
  dateGranted,
}) {
  return (
    <div className="pnsa-page disclosure-doc text-[13px] leading-relaxed text-slate-900" data-testid="pnsa-page">
      {/* Header */}
      <div className="text-center border-b-2 border-slate-900 pb-3 mb-5 break-inside-avoid">
        <h1 className="text-base font-bold tracking-wide">PROMISSORY NOTE AND SECURITY AGREEMENT</h1>
        <div className="mt-1 flex items-center justify-center gap-2 text-[13px]">
          <span className="font-semibold">No.</span>
          <input
            type="text"
            value={form.pnNumber}
            onChange={(e) => set("pnNumber", e.target.value)}
            placeholder="PN number"
            data-testid="pnsa-number"
            className="inline-block min-w-[180px] bg-transparent border-b border-slate-400 outline-none px-1 text-[13px] text-slate-900 placeholder:text-slate-300 focus:bg-yellow-50"
          />
        </div>
      </div>

      <p className="mb-4 text-justify">{PARA_1}</p>

      {/* Loan terms summary (from simulator) */}
      <table className="w-full border border-slate-900 border-collapse text-[12px] mb-4">
        <tbody>
          <tr>
            <td className="border border-slate-900 p-2 w-[45%] font-semibold">Date Granted</td>
            <td className="border border-slate-900 p-2 text-right tabular-nums" data-testid="pnsa-date-granted">{formatDate(dateGranted)}</td>
          </tr>
          <tr>
            <td className="border border-slate-900 p-2 w-[45%] font-semibold">Loan Granted (Amount financed)</td>
            <td className="border border-slate-900 p-2 text-right tabular-nums" data-testid="pnsa-loan-granted">{formatPhp(loanAmount)}</td>
          </tr>
          <tr>
            <td className="border border-slate-900 p-2">Finance Charges (Interest {contractualRatePct}% add-on per month)</td>
            <td className="border border-slate-900 p-2 text-right tabular-nums" data-testid="pnsa-finance-charges">{formatPhp(interestAmount)}</td>
          </tr>
          <tr>
            <td className="border border-slate-900 p-2">Documentary Stamps (DST)</td>
            <td className="border border-slate-900 p-2 text-right tabular-nums" data-testid="pnsa-dst">{formatPhp(dst)}</td>
          </tr>
          <tr>
            <td className="border border-slate-900 p-2">Net Proceeds of Loan</td>
            <td className="border border-slate-900 p-2 text-right tabular-nums" data-testid="pnsa-net-proceeds">{formatPhp(netProceeds)}</td>
          </tr>
          <tr>
            <td className="border border-slate-900 p-2 font-semibold">Effective Interest Rate</td>
            <td className="border border-slate-900 p-2 text-right tabular-nums" data-testid="pnsa-eir">{eirPct}%</td>
          </tr>
          <tr>
            <td className="border border-slate-900 p-2">Schedule of Payment</td>
            <td className="border border-slate-900 p-2" data-testid="pnsa-schedule">
              {form.termMonths} monthly installments of {formatPhp(monthlyAmort)} each, starting {formatDate(firstPaymentDate)}
            </td>
          </tr>
        </tbody>
      </table>

      <p className="mb-6 text-justify">{PARA_2}</p>

      {/* Signatures */}
      <div className="break-inside-avoid">
        <p className="font-semibold">CEBUANA LHUILLIER RURAL BANK, INC.</p>
        <div className="w-2/3">
          <SigLine caption="By: (Signature over printed name) — Authorized Representative" value="" />
        </div>

        <div className="mt-8">
          <SigLine caption="BORROWER/GRANTOR (Signature over printed name)" value={form.name} />
          <div className="mt-4">
            <span className="font-semibold">ADDRESS: </span>
            <span data-testid="pnsa-address">{form.address || "\u00A0"}</span>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <span className="font-semibold">E-MAIL ADDRESS: </span>
            <input
              type="text"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="email@example.com"
              data-testid="pnsa-email"
              className="inline-block min-w-[220px] bg-transparent border-b border-slate-400 outline-none px-1 text-[13px] text-slate-900 placeholder:text-slate-300 focus:bg-yellow-50"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
